<template>
  <Card cardClasses="border-0 shadow-sm rounded-4">
    <template #header>
      <div class="d-flex align-items-center justify-content-between p-3">
        <div class="d-flex align-items-center">
          <div class="icon-circle bg-primary text-white me-3">
            <vue-feather type="users" size="20"></vue-feather>
          </div>
          <div>
            <h4 class="mb-0 fw-bold text-primary">Casual Payroll Details</h4>
            <p class="text-muted mb-0 small">Manage payments for casual workers</p>
          </div>
        </div>
        <div>
          <Badge pill :type="payroll.status === 'draft' ? 'danger' : 'success'" class="badge-lg">
            <vue-feather :type="payroll.status === 'draft' ? 'edit' : 'check-circle'" size="14" class="me-1"></vue-feather>
            {{ payroll.status }}
          </Badge>
        </div>
      </div>
    </template>

    <div class="row p-3">
      <div class="col-md-12">
        <div class="alert alert-light border rounded-4 shadow-sm">
          <div class="d-flex align-items-center">
            <div class="icon-circle-sm bg-info text-white me-3">
              <vue-feather type="calendar" size="16"></vue-feather>
            </div>
            <h5 class="mb-0 fw-bold">{{ payroll.code }} - {{ formatDate(payroll.from) }} to {{ formatDate(payroll.to) }}</h5>
          </div>
        </div>
      </div>

      <div class="col-md-12 mt-3">
        <div class="card border-0 shadow-sm rounded-4">
          <div class="card-header bg-light d-flex justify-content-between align-items-center">
            <h6 class="mb-0 fw-bold">
              <vue-feather type="list" size="16" class="me-2"></vue-feather>
              Casual Workers ({{payroll.payslips.length}})
            </h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                <tr>
                  <th scope="col">Casual Code</th>
                  <th scope="col">Casual Name</th>
                  <th scope="col">Daily Pay</th>
                  <th scope="col">Days</th>
                  <th scope="col">Total</th>
                  <th scope="col">Advance</th>
                  <th scope="col">Gross</th>
                  <th scope="col">Deduction</th>
                  <th scope="col">NET</th>
                  <th></th>
                </tr>
                </thead>
                <tbody v-if="!loading">
                <tr v-for="payslip in payroll.payslips" :key="payslip.id">
                  <td>{{ payslip.casual.casual_no }}</td>
                  <td><a :href="`/casual/${payslip.casual.id}`" class="text-decoration-none">{{ casualName(payslip.casual) }}</a></td>
                  <td>{{ Ksh.format(payslip.pay) }}</td>
                  <td>{{ payroll.days }}</td>
                  <td>{{ Ksh.format(payroll.days * payslip.pay) }}</td>
                  <td>{{ Ksh.format(payslip.advance) }}</td>
                  <td>{{ Ksh.format(payslip.gross) }}</td>
                  <td>{{ Ksh.format(payslip.deduction) }}</td>
                  <td>{{ Ksh.format(payslip.net) }}</td>
                  <td>
                    <button
                        v-if="payroll.status === 'draft' && profile.role !== 'user'"
                        class="btn btn-danger btn-sm rounded-pill"
                        @click="confirmDialog('Payslip', () => deletePayslip(payslip.id))">
                      <vue-feather type="trash-2" size="14" class="me-1"></vue-feather>
                      Delete
                    </button>
                  </td>
                </tr>
                <tr class="table-light">
                  <td colspan="2" class="text-uppercase fw-bold">Total:</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'pay') }}</td>
                  <td></td>
                  <td class="fw-bold">{{ incomeTotal(payroll.payslips, 'pay', payroll.days) }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'advance') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'gross') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'deduction') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'net') }}</td>
                  <td></td>
                </tr>
                </tbody>
              </table>
              <div v-if="loading" class="text-center py-5">
                <Spinner spinner-class="mb-1" />
                <p class="text-muted">Loading payroll data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-12 d-flex justify-content-center mt-4 pt-3 border-top">
        <button
            v-if="payroll.status !== 'draft' && profile.role !== 'user' && profile.role !== 'admin' && payroll.payout === null && integrationTypes.includes('mpesa')"
            class="btn btn-info rounded-pill me-3"
            @click="confirmDialog('Make Payments', () => createPayouts(), 'Do you want to make payments for this payroll? This will initiate bank payouts.')"
        >
          <vue-feather type="credit-card" size="16" class="me-1"></vue-feather>
          Make Payments
        </button>
        <button
            v-if="payroll.status !== 'draft' && payroll.payout !== null"
            class="btn btn-success rounded-pill me-3"
            @click="$router.push('/casualPayrollPayouts/' + $route.params.id)"
        >
          <vue-feather type="dollar-sign" size="16" class="me-1"></vue-feather>
          Payout Details
        </button>
        <button
            v-if="payroll.status === 'draft' && profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary rounded-pill me-3"
            @click="confirmDialog('Confirm Payroll', () => confirmPayroll(), 'Do you want to confirm this payroll?')"
        >
          <vue-feather type="check-circle" size="16" class="me-1"></vue-feather>
          Actualize Payments
        </button>
        <button class="btn btn-outline-dark rounded-pill me-3" @click="$router.go(-1)">
          <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
          Back
        </button>
        <button
            v-if="payroll.status === 'draft' && profile.role !== 'user'"
            class="btn btn-danger rounded-pill"
            @click="confirmDialog('Payroll', () => deletePayroll())"
        >
          <vue-feather type="trash-2" size="16" class="me-1"></vue-feather>
          Delete Payments
        </button>
      </div>
    </div>
  </Card>
</template>

<script>
import casualPayrollService from "@/api/casualPayrollService";
import formatDate from "../../utils/formatDate";
import confirmDialog from "@/utils/confirmDialog";
import {decrypt} from "@/utils/crypto";
import payoutService from "@/api/payoutService";
import VueFeather from 'vue-feather';

let Ksh = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2
});

export default {
  name: "CasualDetails",
  components: {
    VueFeather
  },
  data() {
    return {
      profile: {},
      loading: true,
      payroll: {
        payslips: [],
      },
      Ksh,
      organization: {},
      integrationTypes: []
    }
  },
  methods: {
    confirmDialog,
    formatDate,
    async getPayroll() {
      this.payroll = await casualPayrollService.getPayroll(this.$route.params.id)
      this.loading = false
    },
    casualName(casual) {
      let middleName = casual.middle_name === null ? ' ' : casual.middle_name
      return casual.first_name + " " + middleName + " " + casual.last_name
    },
    totalItems(payslips, item) {
      let total = 0
      for (const payslip of payslips) {
        total += payslip[item]
      }
      return Ksh.format(total)
    },
    incomeTotal(payslips, item, days) {
      let total = 0
      for (const payslip of payslips) {
        total += payslip[item] * days
      }
      return Ksh.format(total)
    },
    async confirmPayroll() {
      await casualPayrollService.confirmPayroll(this.$route.params.id)
    },
    async createPayouts() {
      await payoutService.createCasualPayout(this.$route.params.id)
    },
    async deletePayslip(id) {
      await casualPayrollService.deletePayslip(id)
      await this.getPayroll()
    },
    async deletePayroll() {
      await casualPayrollService.deletePayroll(this.$route.params.id)
    },
  },
  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.organization.integrations.forEach(integration => {
      this.integrationTypes.push(integration.type)
    })
    await this.getPayroll()
    localStorage.setItem('casualPayrollStatus', this.payroll.status)
  },
}
</script>

<style scoped>
/* Table styles */
table td,
table th {
  white-space: nowrap;
  width: 1%;
}

/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

/* Button hover effects */
.btn-primary.rounded-pill:hover,
.btn-outline-dark.rounded-pill:hover,
.btn-success.rounded-pill:hover,
.btn-info.rounded-pill:hover,
.btn-danger.rounded-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Card styles */
.card {
  transition: all 0.3s ease;
}

.alert {
  transition: all 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .col-md-12 {
    margin-bottom: 1rem;
  }
}
</style>
